import Big from 'big.js'
import * as TokenSale from '../contracts/master'
import {
  IMasterParams,
  IMasterTimeParams,
  Sold,
  VestingPoints,
} from '../types/reducerTypes'
import { amountFromWei } from '../utils/amount'
import LPTokenContract from './useLPTokenContract'
import { config, networks } from '../config'
import { ethers } from 'ethers'

export interface IStakes {
  tier: string
  amount: Big
  share: string
  piece: boolean
}

export enum Epoch {
  Incoming,
  Private,
  Waiting,
  Public,
  Finished,
}

export interface IMasterRpcContract {
  exchangeRate: (address: string, abi?: ethers.InterfaceAbi) => Promise<Big>
  params: (address: string) => Promise<IMasterParams>
  oldParams: (
    address: string,
    abiabiMaster: ethers.InterfaceAbi,
    interfaceMasterContractPool: any,
    abiToken?: ethers.InterfaceAbi,
  ) => Promise<IMasterParams>
  totalSold: (address: string) => Promise<Sold>
  oldTotalSold: (address: string, abi: ethers.InterfaceAbi) => Promise<Sold>
  time: (address: string) => Promise<IMasterTimeParams>
  oldTime: (
    address: string,
    abi: ethers.InterfaceAbi,
  ) => Promise<IMasterTimeParams>
}

const useMasterRpcContract = (
  provider: ethers.BrowserProvider | ethers.JsonRpcProvider,
  abi?: ethers.InterfaceAbi,
  addressForOldAbi?: string,
): IMasterRpcContract => {
  const { getNameSymbol, getDecimals } = LPTokenContract(provider)

  async function exchangeRate(address: string, abi?: ethers.InterfaceAbi) {
    const exchange = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
      provider,
    )
    const rate = await exchange?.exchangeRate()
    return rate
  }

  async function params(address: string): Promise<IMasterParams> {
    const contractParams =
      abi && addressForOldAbi
        ? new ethers.Contract(address, abi as ethers.InterfaceAbi)
        : new ethers.Contract(address, TokenSale.abi as ethers.InterfaceAbi)

    const params = await contractParams.getParams()
    const { tokenName, tokenSymbol } = await getNameSymbol(params[1])
    const decimals = Number(await getDecimals(params[1]))

    const totalSaleSupply = amountFromWei(String(params[2]), decimals)
    const privateTokenPrice = amountFromWei(String(params[3]), decimals)
    const publicTokenPrice = amountFromWei(String(params[4]), decimals)
    const thresholdPublicAmount = amountFromWei(String(params[6]), decimals)
    const escrowPercentageCounted = amountFromWei(
      new Big(new Big(params[2]).mul(params[5])).div(100),
      decimals,
    ) //(Number(totalSaleSupply) * Number(params[5])) / 100;
    const airDrop = new Big(amountFromWei(String(params[7]), decimals))

    const supplyCounted =
      // amountFromWei(
      // new Big(
      await contractParams
        ?.saleTokensAmountWithoutAirdrop()

        .catch((err: any) => console.error(err, address))

    // );
    // decimals,
    // );
    const poolSupplyCounted = supplyCounted
      ? amountFromWei(new Big(supplyCounted), decimals)
      : new Big(0)

    const vestingPoints: VestingPoints[] = params[10].map((item: string[]) => ({
      timestamp: item[0] + '000',
      pct: item[1],
    }))

    return {
      initialAddress: params[0],
      token: params[1],
      totalSaleSupply,
      poolSupplyCounted,
      decimals,
      publicTokenPrice,
      privateTokenPrice,
      escrowPercentage: params[5],
      thresholdPublicAmount,
      tierPrices:
        config.net === networks.mainnet
          ? 0
          : params[8].map((tp: string) => new Big(tp)),
      escrowReturnMilestones: params[9],
      escrowPercentageCounted,
      airDrop,
      tokenName,
      tokenSymbol,
      vestingPoints,
    }
  }

  async function totalSold(address: string): Promise<Sold> {
    const sold = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
    )
    const totalPrivateSold = new Big(
      // await sold.totalPrivateSold(),
      0,
    )
    const totalPublicSold = new Big(
      // await sold.totalPublicSold(),
      0,
    )
    return {
      total: totalPrivateSold.add(totalPublicSold),
      private: totalPrivateSold,
      public: totalPublicSold,
    }
  }

  async function oldTotalSold(
    address: string,
    abi: ethers.InterfaceAbi,
  ): Promise<Sold> {
    const sold = new ethers.Contract(address, abi as ethers.InterfaceAbi)
    const totalPrivateSold = new Big(await sold.totalPrivateSold())
    const totalPublicSold = new Big(await sold.totalPublicSold())
    return {
      total: totalPrivateSold.add(totalPublicSold),
      private: totalPrivateSold,
      public: totalPublicSold,
    }
  }

  async function time(address: string) {
    const sold = new ethers.Contract(
      address,
      TokenSale.abi as ethers.InterfaceAbi,
    )
    const timeParams = await sold.getTimeParams()
    return {
      privatePoolSaleStartTime: timeParams[0],
      privatePoolSaleEndTime: timeParams[1],
      publicPoolSaleStartTime: timeParams[2],
      publicPoolSaleEndTime: timeParams[3],
      democracyHourEndTimestamp: timeParams[4],
    }
  }

  async function oldTime(address: string, abi: ethers.InterfaceAbi) {
    const sold = new ethers.Contract(address, abi as ethers.InterfaceAbi)
    const timeParams = await sold.getTimeParams()
    return {
      privatePoolSaleStartTime: timeParams[0],
      privatePoolSaleEndTime: timeParams[1],
      publicPoolSaleStartTime: timeParams[2],
      publicPoolSaleEndTime: timeParams[3],
      democracyHourEndTimestamp: timeParams[4],
    }
  }

  async function oldParams(
    address: string,
    abiMaster: ethers.InterfaceAbi,
    interfaceMasterContractPool: any,
    abiToken?: ethers.InterfaceAbi,
  ) {
    const contractParams = new ethers.Contract(
      address,
      abiMaster as ethers.InterfaceAbi,
    )
    const params = await contractParams.getParams()
    const { tokenName, tokenSymbol } = await getNameSymbol(
      params[interfaceMasterContractPool.token],
      abiToken,
    )
    const decimals = Number(
      await getDecimals(params[interfaceMasterContractPool.token]),
    )

    const totalSaleSupply = amountFromWei(
      String(params[interfaceMasterContractPool?.totalSupply]),
      decimals,
    )
    const privateTokenPrice = amountFromWei(
      String(params[interfaceMasterContractPool?.privateTokenPrice]),
      decimals,
    )
    const publicTokenPrice = amountFromWei(
      String(params[interfaceMasterContractPool?.publicTokenPrice]),
      decimals,
    )
    const publicRoundBuyLimit = amountFromWei(
      String(params[interfaceMasterContractPool?.thresholdPublicAmount]),
      decimals,
    )
    const thresholdPublicAmount = amountFromWei(
      String(params[interfaceMasterContractPool?.thresholdPublicAmount]),
      decimals,
    )
    const escrowPercentageCounted = new Big(totalSaleSupply)
      .mul(params[interfaceMasterContractPool.escrowPercentage])
      .div(100)
    //(Number(totalSaleSupply) * Number(params[6])) / 100;
    const airDrop = new Big(0)
    //  = interfaceMasterContractPool.airDrop
    //   ? new Big(params[interfaceMasterContractPool.airDrop])
    //   : new Big(totalSaleSupply).minus(new Big(escrowPercentageCounted)).mul(1).div(100);
    const poolSupplyCounted = new Big(totalSaleSupply)
      .minus(escrowPercentageCounted)
      .minus(airDrop)

    const vestingPoints: VestingPoints[] = params[10]
      ? params[10].map((item: string[]) => ({
          timestamp: item[0] + '000',
          pct: item[1],
        }))
      : []

    return {
      initialAddress: params[interfaceMasterContractPool.initial],
      token: params[interfaceMasterContractPool.token],
      totalSaleSupply,
      poolSupplyCounted,
      decimals,
      publicTokenPrice,
      privateTokenPrice,
      publicRoundBuyLimit,
      escrowPercentage: params[interfaceMasterContractPool.escrowPercentage],
      thresholdPublicAmount,
      tierPrices:
        config.net === networks.mainnet
          ? 0
          : params[8].map((tp: string) => new Big(tp)),
      escrowReturnMilestones:
        params[interfaceMasterContractPool.escrowReturnMilestones],
      escrowPercentageCounted,
      airDrop,
      tokenName,
      tokenSymbol,
      claimTime: params[interfaceMasterContractPool?.claimTime],
      vestingPoints,
    }
  }

  return {
    exchangeRate,
    params,
    oldParams,
    oldTotalSold,
    totalSold,
    time,
    oldTime,
  }
}

export default useMasterRpcContract
